import moment from "moment";
import "moment/min/locales";
import { t } from "i18next";

import { CUSTOM_DATE_FORMATS } from "../constants/customDateFormats";

export const getYearAndQuarter = (date, language = "en") => {
  return `Q${moment(date).quarter()} ${moment(date).year()}`;
};

export const getYearAndPreviousQuarter = (date, language = "en") => {
  let prevQuarter = moment(date).subtract(1, "quarter");
  return `Q${moment(prevQuarter).quarter()} ${moment(prevQuarter).year()}`;
};

export const getLongDate = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.LongDateWithShortMonth
  );
};

export const getShortDate = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.ShortMonthShortYear
  );
};

export const getMonthDayYearDate = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.MonthDayYear
  );
};

export const getShortMonthLongYearDate = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.ShortMonthLongYear
  );
};

export const getYearMonthDay = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.YearMonthDay
  );
};

export const getLongMonthLongYearDate = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.LongMonthLongYear
  );
};

export const getUtcDateTime = (dateTime) => {
  return moment.utc(dateTime).format(CUSTOM_DATE_FORMATS.DateTime);
};

export const getLongDateTime = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.LongDateTime
  );
};

export const getLongUTCDateTime = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).utc().format(
    CUSTOM_DATE_FORMATS.LongDateTime
  );
};

export const getFullDateISO = (date, language = "en") => {
  return moment(date, moment.ISO_8601, language).format(
    CUSTOM_DATE_FORMATS.FullDateYearISO
  );
};

export const getUnixTimestamp = (date) => {
  if (date) return moment.utc(date).valueOf();

  return moment.utc().valueOf();
};

export const getTextDuration = (totalMinutes, abbreviated = false) => {
  if (!Number.isInteger(totalMinutes)) {
    return "";
  }

  const minutes = totalMinutes % 60;
  const hours = (totalMinutes - minutes) / 60;

  if (abbreviated) {
    if (hours > 0 && minutes > 0) {
      return (
        t("services.dateService.hrs", { count: hours }) +
        " " +
        t("services.dateService.mins", { count: minutes })
      );
    } else if (hours > 0) {
      return t("services.dateService.hrs", {
        count: hours,
      });
    } else {
      return t("services.dateService.mins", {
        count: minutes,
      });
    }
  }

  if (hours > 0 && minutes > 0) {
    return t("services.dateService.duration", {
      hours: hours,
      minutes: minutes,
      comma: 1,
    });
  } else if (hours > 0) {
    return t("services.dateService.hours", { count: hours });
  } else {
    return t("services.dateService.minutes", { count: minutes });
  }
};

// Converts a date to the local twelve hour time format (e.g. "06:00 AM" or "06:00 PM")
// If no date is provided, it returns the current local time
export const getTwelveHourTime = (date, language = "en") => {
  moment.locale(language);
  return moment
    .utc(date || undefined)
    .local()
    .format(CUSTOM_DATE_FORMATS.TwelveHourTime);
};

export const getLocalDateTime = (date, language = "en") => {
  moment.locale(language);
  return moment
    .utc(date || undefined)
    .local()
    .format(CUSTOM_DATE_FORMATS.DateTime);
};

export const getTimeDiffFromNow = (date, language = "en", showAgo = true) => {
  moment.locale(language);
  return moment(date || undefined).fromNow(showAgo);
};

export const getDaysBetween = (date1, date2) => {
  const normalizedDate1 = new Date(date1).setHours(0, 0, 0, 0);
  const normalizedDate2 = new Date(date2).setHours(0, 0, 0, 0);
  const diff = normalizedDate2 - normalizedDate1;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

//this function will return the date in the format of "today 03:33 pm", "yesterday 03:33 pm" or the long date "11-Dec-2023 03:33 pm"
export const getLongDateTimeWithDayPrecision = (date, language = "en") => {
  if (!date) return {};

  const today = new Date();
  const daysDiff = getDaysBetween(date, today);

  if (daysDiff === 0) {
    return t("services.dateService.todayDatetime", {
      time: getTwelveHourTime(date),
    });
  } else if (daysDiff === 1) {
    return t("services.dateService.yesterdayDatetime", {
      time: getTwelveHourTime(date),
    });
  } else {
    return getLongDateTime(date, language);
  }
};
